import React from 'react';
import { motion } from 'framer-motion';
import Footer from './Footer';
import Header from './Header';
import '../css/AboutPage.css';
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { BookOpen, Target, Users, Award, FileText, Download } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const handleGetStarted = () => {
    if (auth.isAuthenticated) {
      navigate("/account");
    } else {
      auth.signinRedirect();
    }
  };

  return (
    <div className="about-page-wrapper">
      <Header />
      <main className="about-main-content">
      {/* Hero Section */}
      <section className="about-hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="about-hero-content"
        >
          <h1 className="about-hero-title">About SmartKids Prep</h1>
          <p className="about-hero-subtitle">
            Empowering elementary school students to excel in standardized tests through comprehensive, free practice resources
          </p>
        </motion.div>
      </section>

      <div className="about-container">
        {/* Mission Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="about-section"
        >
          <h2 className="section-title">Our Mission</h2>
          <p className="about-text">
            Welcome to <strong>SmartKids Prep</strong>, a comprehensive platform designed to help elementary school students prepare for important standardized tests such as <strong>IAAT</strong>, <strong>NNAT</strong>, and <strong>COGAT</strong>. We believe that every child deserves access to high-quality test preparation resources, which is why all our features are completely <strong>free</strong>.
          </p>
          <p className="about-text">
            Our mission is to make test preparation accessible, engaging, and effective for students and their families. We provide thousands of practice questions and interactive tools to help children build confidence and achieve their academic goals.
          </p>
        </motion.section>

        {/* Features Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="about-section"
        >
          <h2 className="section-title">What We Offer</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <FileText size={32} />
              </div>
              <div className="feature-item-content">
                <h3 className="feature-item-title">Online Practice Tests</h3>
                <p className="feature-item-text">
                  Take practice tests directly on our website using our interactive online tool. Get instant feedback and track your progress in real-time.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Download size={32} />
              </div>
              <div className="feature-item-content">
                <h3 className="feature-item-title">Printable PDF Tests</h3>
                <p className="feature-item-text">
                  Generate PDF versions of practice tests for offline studying. Perfect for reducing screen time while still getting quality practice.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <BookOpen size={32} />
              </div>
              <div className="feature-item-content">
                <h3 className="feature-item-title">Thousands of Questions</h3>
                <p className="feature-item-text">
                  Access a comprehensive library of test questions covering all major test formats and difficulty levels.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Target size={32} />
              </div>
              <div className="feature-item-content">
                <h3 className="feature-item-title">Progress Tracking</h3>
                <p className="feature-item-text">
                  Monitor your child's improvement with detailed performance reports and accuracy metrics by question type.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Award size={32} />
              </div>
              <div className="feature-item-content">
                <h3 className="feature-item-title">Expert-Curated Content</h3>
                <p className="feature-item-text">
                  All questions are carefully selected to match real test formats and help students understand what to expect.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Users size={32} />
              </div>
              <div className="feature-item-content">
                <h3 className="feature-item-title">Completely Free</h3>
                <p className="feature-item-text">
                  No hidden fees, no subscriptions. All features are available at no cost to help every child succeed.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* How to Get Started */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="about-section"
        >
          <h2 className="section-title">Getting Started is Easy</h2>
          <p className="about-text">
            Creating an account is quick and simple! You can sign up in two easy ways:
          </p>
          
          <div className="signup-options">
            <div className="signup-option">
              <div className="signup-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div className="signup-option-content">
                <h3 className="signup-option-title">Sign in with Google</h3>
                <p className="signup-option-text">Quick and easy sign-in using your Google account</p>
              </div>
            </div>
            
            <div className="signup-divider">
              <span>OR</span>
            </div>
            
            <div className="signup-option">
              <div className="signup-option-icon">
                <Users size={24} />
              </div>
              <div className="signup-option-content">
                <h3 className="signup-option-title">Create Your Account</h3>
                <p className="signup-option-text">Sign up with your email to create a new account</p>
              </div>
            </div>
          </div>
          
          <p className="about-text">
            Once you're logged in, you can immediately start creating practice tests, track your progress, and take full advantage of all the resources SmartKidsPrep.com has to offer.
          </p>
        </motion.section>

        {/* Why Choose Us */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="about-section"
        >
          <h2 className="section-title">Why Choose SmartKids Prep?</h2>
          <ul className="about-list-enhanced">
            <li><strong>100% Free:</strong> All features and resources are available at no cost</li>
            <li><strong>Comprehensive Coverage:</strong> Practice materials for IAAT, NNAT, COGAT, and more</li>
            <li><strong>Flexible Learning:</strong> Practice online or download PDFs for offline study</li>
            <li><strong>Progress Tracking:</strong> Detailed reports help identify strengths and areas for improvement</li>
            <li><strong>User-Friendly:</strong> Simple interface designed for both kids and parents</li>
            <li><strong>Expert Quality:</strong> Questions curated to match real test formats</li>
          </ul>
        </motion.section>

        {/* Call to Action Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="about-call-to-action"
        >
          <h3 className="call-to-action-title">Ready to Get Started?</h3>
          <p className="call-to-action-text">
            Join thousands of students who are already preparing for success with SmartKids Prep!
          </p>
          <div className="cta-button-wrapper">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGetStarted}
              className="cta-button-large"
              type="button"
            >
              {auth.isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
            </motion.button>
          </div>
          {!auth.isAuthenticated && (
            <p className="cta-subtext">
              Click the button above to create your free account and begin practicing today!
            </p>
          )}
        </motion.div>
      </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
